'use client';

import { Box, Flex, freddyTheme, Typography } from '@coolblue-development/freddy';
import clickItemTrigger from '@coolblue-development/ga4-events/triggers/OverarchingProductLine/clickItemTrigger';
import showTrigger from '@coolblue-development/ga4-events/triggers/OverarchingProductLine/showTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { TrackImmediate } from '@coolblue-development/next-analytics/client';
import { Image } from '@coolblue-development/next-image/client';
import { SelectableBox, SelectableBoxGroup } from '@coolblue-development/next-patterns/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { AvailabilityState } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { getCollectionProductImageId } from '@utils/collectionUtils/getCollectionProductImageId';
import { useRouter } from 'next/navigation';

import { OverarchingCollectionType } from '../Collection.types';

type OverarchingCollectionProps = {
  collection: OverarchingCollectionType;
};

export const OverarchingCollection = ({ collection }: OverarchingCollectionProps) => {
  const { t } = useTranslation('webshop');
  const { trackEvent } = useAnalytics();
  const { entranceDescription, entranceTitle, title, products } = collection;
  const product =
    products &&
    products.find(
      product =>
        product !== null &&
        product.urls !== null &&
        product.mediaGallery !== null &&
        product.nullableAvailabilityState &&
        product?.nullableAvailabilityState !== AvailabilityState.OutOfStock &&
        product?.nullableAvailabilityState !== AvailabilityState.TemporaryOutOfStock
    );

  const router = useRouter();
  const image = product ? getCollectionProductImageId(product) : null;
  const { isInternalUser } = useRequestContext();
  const testId = isInternalUser ? { 'data-cy': 'overarching-collection' } : {};

  if (!product) {
    return <></>;
  }

  return (
    <Box {...testId}>
      <TrackImmediate trigger={showTrigger()} />
      <Typography
        variant='h3'
        fontSize={{ xs: 'md', lg: 'xl' }}
        lineHeight={{ xs: 400, lg: 500 }}
        mb={100}
      >
        {title}
      </Typography>
      <Box
        sx={{
          'input[type="radio"]:checked+label': {
            borderColor: freddyTheme.color.border.interactiveBase,
            backgroundColor: 'initial',
            borderWidth: freddyTheme.borderWidth[25],
            padding: freddyTheme.spacing[400]
          }
        }}
      >
        <SelectableBoxGroup
          name={entranceTitle}
          type='radio'
          inputPlacement='hidden'
          spacing='default'
        >
          <SelectableBox
            value={entranceTitle}
            onClick={() => {
              trackEvent(
                clickItemTrigger({
                  index: 1,
                  total_indices: 1,
                  item_id: product?.id ?? 0
                })
              );

              router.push(product?.urls.product || '');
            }}
          >
            <Flex direction='row-reverse' justifyContent='flex-end' gap={400}>
              <Box>
                <Typography display='inline' variant='h5' color='interactiveBase' m={0}>
                  {entranceTitle}
                </Typography>
                <Typography ml={200} display='inline' color='sentimentPositive'>
                  {t('productPage.collection.betterModel')}
                </Typography>
                <Typography>{entranceDescription}</Typography>
              </Box>
              {product && image && (
                <Image
                  imageId={image}
                  fileCollection='product'
                  width={64}
                  height={64}
                  background='transparent'
                  alt={product.nullableName ? product.nullableName : entranceTitle}
                  loading='lazy'
                />
              )}
            </Flex>
          </SelectableBox>
        </SelectableBoxGroup>
      </Box>
    </Box>
  );
};
